/**
 * "Unity editor mode" kapcsoló (admin, dev eszköz).
 *
 * Bekapcsolva a 3D nem a beépített WebGL buildel beszél, hanem a fejlesztő SAJÁT GÉPÉN futó Unity
 * Editorral, annak dev bridge-én át (lásd docs/unity-bridge/).
 *
 * A 127.0.0.1 mindig a BÖNGÉSZŐT futtató gépet jelenti, nem a webszervert: ez a kód a böngészőben
 * fut, a szerver csak kiszolgálja a bundle-t. Ezért működik akkor is, ha az oldalt staging adja —
 * a szerverre nincs mit telepíteni, és a hídból semmit nem lát.
 *
 * A tárolás sessionStorage, a viewOptions.ts mintájára: túléli az oldal-újratöltést (híddal
 * fejlesztés közben ez folyamatos), de böngészőzáráskor törlődik.
 */

const MODE_KEY = 'ucbridge:enabled';
const URL_KEY = 'ucbridge:url';

const DEFAULT_URL = 'http://127.0.0.1:8787';

/**
 * A bridge címe. Alapból a doksi szerinti port; sessionStorage-ből felülírható, ha valakinél foglalt
 * a 8787 — konzolból `sessionStorage.setItem('ucbridge:url', 'http://127.0.0.1:9000')`. Szándékosan
 * nincs hozzá UI: ritka eset, és a hibás érték némán elrontaná a próbát.
 *
 * http, nem https: a böngésző a loopbacknek külön kivételt ad, így nem kell tanúsítványt gyártani.
 */
export const UNITY_EDITOR_URL: string = readStorage(URL_KEY) || DEFAULT_URL;

function readStorage(key: string): string | null {
    try {
        return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
    } catch {
        return null;   /* privát mód / letiltott storage */
    }
}

export const isUnityEditorModeEnabled = (): boolean => readStorage(MODE_KEY) === 'true';

/**
 * Beállítja a módot és ÚJRATÖLTI az oldalt. A mód mount-idejű döntés: bridge módban a WebGL build be
 * sem töltődik (nincs wasm-letöltés, nincs canvas), tehát menet közben nem lehet átbillenteni.
 */
export const setUnityEditorMode = (enabled: boolean): void => {
    try {
        sessionStorage.setItem(MODE_KEY, enabled ? 'true' : 'false');
    } catch {
        /* storage nincs — ilyenkor a mód nem tud bekapcsolni, az újratöltés után alapállapot lesz */
    }
    window.location.reload();
};

/**
 * Fut-e egyáltalán Editor a másik végen. Bekapcsolás ELŐTT hívjuk: enélkül újratöltenénk az oldalt,
 * és az eredmény "sem canvas, sem Unity" lenne — a legrosszabb fajta néma hiba.
 */
export const probeUnityEditor = async (): Promise<boolean> => {
    const abort = new AbortController();
    const timer = window.setTimeout(() => abort.abort(), 1000);
    try {
        const response = await fetch(`${UNITY_EDITOR_URL}/health`, { signal: abort.signal });
        return response.ok;
    } catch {
        return false;   /* nem fut, rossz port, vagy a böngésző blokkolta (mixed content / PNA) */
    } finally {
        window.clearTimeout(timer);
    }
};
